import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { getCampaignTypeData } from "./steps";
import {
  setStep,
  initRecipients,
  initCurrentEmailCampaignData,
  initNewsletterBlocks,
  setCurrentDndTheme,
  setCurrentNewsletterTemplate,
  setCurrentLayout,
  setCurrentEmailCampaignData,
  setNewsletterBlocks,
  setTTPDialogCustomData,
} from "actions";

import { CAMPAIGN_TYPES } from "../../../config/Common";
import { fetchCampaign, fetchGenericEmails } from "../../../actions/thunks";
import {
  initGECampaign,
  resolveTargetTypeFromQuery,
} from "../../../services/utils";
import { decomposeNewsletter } from "../../../services/block/newsletter";

import TTPLoader from "../../ui/TTPLoader";
import ActionBar from "./ActionBar";
import DIALOG_TYPES from "../../../constants/TTPDialog";
import { toast } from "react-toastify";
import _ from "i18n";

const CreateCampaignContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams();

  const [state, setState] = useState({
    type: "text",
    steps: [],
    currentStepIndex: 0,
    icon: null,
    title: "",
    updateHash: Math.random() + "",
  });

  const validGeParameters = () => {
    const query = new URLSearchParams(location?.search);
    return (
      query.get("targetApp") &&
      query.get("language") &&
      query.get("targetId") &&
      (query.get("targetType") ||
        (query.get("discriminant") && query.get("rappelType")))
    );
  };

  useEffect(() => {
    const query = new URLSearchParams(location?.search);
    const type = query.get("type") || "text";
    const { steps, icon, title } = getCampaignTypeData(type);

    setState((prevState) => ({
      ...prevState,
      type,
      steps,
      icon,
      title,
    }));

    const isNewsletter = type === "newsletter";
    const campaignType = isNewsletter ? "dnd" : type;

    props.setStep();
    props.initCurrentCampaign({
      type: CAMPAIGN_TYPES[campaignType],
      newsLetter: isNewsletter ? 1 : 0,
    });

    let isMounted = true;

    const initRecipients = async () => {
      await props.initCampaignRecipients();
      if (isMounted) {
        props.setCurrentCampaign({
          recipientType: "",
          eventFilters: [],
          allowedGroups: [],
          deniedGroups: [],
        });
      }
    };

    initRecipients();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location?.search);
    const type = query.get("type") || "text";
    const { steps, icon, title } = getCampaignTypeData(type);

    setState((prevState) => ({
      ...prevState,
      type,
      steps,
      icon,
      title,
    }));

    const isNewsletter = type === "newsletter";
    const campaignType = isNewsletter ? "dnd" : type;

    props.setStep();
    props.initCurrentCampaign({
      type: CAMPAIGN_TYPES[campaignType],
      newsLetter: isNewsletter ? 1 : 0,
    });
    props.initCampaignRecipients();

    if (campaignId) {
      props.fetchCampaign(campaignId).then((res) => {
        const { design, newsLetter, html, fields } = res.value?.data.data[0];

        if (design) {
          props.setCurrentDndTheme({ design });
        }

        if (design && newsLetter === 1) {
          const template = { design, content: html };

          try {
            props.setNewsletterBlocks(JSON.parse(fields));
          } catch (error) {
            decomposeNewsletter(template, (blocks) =>
              props.setNewsletterBlocks(blocks),
            );
          }
          props.setCurrentNewsletterTemplate(template);
        }

        setState((prevState) => ({
          ...prevState,
          currentStepIndex: prevState.steps?.length - 1 || 0,
        }));
      });
    } else if (validGeParameters()) {
      const filters = {
        targetApp: query.get("targetApp"),
        targetType: resolveTargetTypeFromQuery(query),
        language: query.get("language"),
        targetId: query.get("targetId"),
        organization: props.currentClient?.id,
      };

      props
        .fetchGenericEmails(filters)
        .then((res) => {
          const { fromEmail, fromName, language, object, text, theme } =
            res.value.data.data[0];
          const data = {
            type: "PLAIN_TEXT",
            language,
            from: fromEmail,
            fromName,
            theme,
            object: object || "",
            html: text,
            mutatedContent: theme?.content ?? "",
            targetId: parseInt(query.get("targetId")),
            disableRecipientType: filters.targetType === "SURVEY_INVITATION",
            ...initGECampaign(query),
          };

          props.initCurrentCampaign(data);

          if (theme?.design) {
            props.setCurrentDndTheme({ design: theme.design });
          }

          setState((prevState) => ({
            ...prevState,
            currentStepIndex: prevState.steps?.length - 1 || 0,
          }));
        })
        .catch(() => {
          toast.warning(_("Email not found"));
        });
    }
  }, [location.search, campaignId]);

  // Cleanup effect to reset state when component unmounts
  useEffect(() => {
    return () => {
      props.setStep(null);
      props.initCurrentCampaign();
      props.initNewsletterBlocks();
      props.setCurrentDndTheme();
      if (props.initCurrentLayout) {
        props.initCurrentLayout();
      }
      if (props.setCurrentNewsletterTemplate) {
        props.setCurrentNewsletterTemplate();
      }
    };
  }, []);

  const onStepChange = (currentStepIndex) => {
    setState((prevState) => ({
      ...prevState,
      currentStepIndex,
    }));
  };

  const confirmClose = () => {
    if (childRef && childRef.close) {
      childRef.close();
    } else {
      props.setTTPDialogCustomData({
        approvedAction: close,
        message: _(
          "Once you close your changes will be lost. Do you really want to continue?",
        ),
        type: DIALOG_TYPES.ALERT,
      });
    }
  };

  const close = () => {
    props.setStep(null);
    navigate("/campaign/selector");
  };

  const { steps, currentStepIndex, type, icon, title } = state;
  const { currentCampaignFetching, updating } = props;

  const Content =
    steps && steps[currentStepIndex] ? steps[currentStepIndex].body : null;

  const [childRef, setChildRef] = useState(null);

  const onAction = (actionCode) => {
    const action = childRef && childRef[actionCode];
    if (action) action();
  };

  const proceed = (step) => {
    if (currentStepIndex < steps.length - 1) {
      onStepChange(step ?? currentStepIndex + 1);
    }
  };

  return (
    <div className="create-campaign">
      <ActionBar
        icon={icon}
        title={title}
        currentStepIndex={currentStepIndex}
        steps={steps}
        onChange={onStepChange}
        onClose={confirmClose}
        campaignType={type}
        onAction={onAction}
        isSaved={childRef?.isSaved?.()}
        isUpdating={updating}
      />

      <div className="create-campaign__body">
        {currentCampaignFetching && <TTPLoader />}
        {!currentCampaignFetching && Content && (
          <Content
            {...props}
            proceed={proceed}
            inCreateCampaign={true}
            childRef={setChildRef}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (store) => ({
  currentCampaignFetching:
    store.currentCampaign.email.fetching ||
    store.genericEmail.currentSetting.emails.fetching,
  currentCampaign: store.currentCampaign.email,
  currentClient: store.auth.currentClient,
  updating: store.currentCampaign.email.updating,
});

const mapDispatchToProps = (dispatch) => ({
  setStep: (step = "CREATE_CAMPAIGN") => dispatch(setStep(step)),
  initCampaignRecipients: () => dispatch(initRecipients()),
  initCurrentCampaign: (data) => dispatch(initCurrentEmailCampaignData(data)),
  setCurrentCampaignData: (data) => dispatch(setCurrentEmailCampaignData(data)),
  initNewsletterBlocks: () => dispatch(initNewsletterBlocks()),
  setCurrentDndTheme: (data = null) => dispatch(setCurrentDndTheme(data)),
  setNewsletterBlocks: (data) => dispatch(setNewsletterBlocks(data)),
  fetchCampaign: (id) => dispatch(fetchCampaign(id)),
  fetchGenericEmails: (filters) => dispatch(fetchGenericEmails(filters)),
  setTTPDialogCustomData: (data) => dispatch(setTTPDialogCustomData(data)),
  initCurrentLayout: () => dispatch(setCurrentLayout(null)),
  setCurrentCampaign: (data) => dispatch(setCurrentEmailCampaignData(data)),
  setCurrentNewsletterTemplate: (data = null) =>
    dispatch(setCurrentNewsletterTemplate(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateCampaignContainer);
